import React, { useEffect } from 'react'
import InputDivider from '../inputs/InputDivider'
import PersonInfo from '../misc/PersonInfo'
import SVGIcon from '../helpers/icons'
import { observable, reaction, action } from 'mobx'
import { observer } from 'mobx-react'
import useSettingsManager from '../hooks/use-settings-manager'
import { LocalizedInput, LocalizedInputRadioBox } from './LocalizedInput'
import FormHeader from '../inputs/FormHeader'
import { AcceptTerms } from './PortOverview'
import { PaymentGateways } from '../PortConfiguratorOperation'
import usePortManager from '../hooks/use-port-manager'
import usePriceManager from '../hooks/use-price-manager'

class CheckoutState {
  @observable accessor company = ''
  @observable accessor is_company = false
  @observable accessor is_building_old = false
  @observable accessor should_pay_vat = false
  @observable accessor vat_number = ''
  @observable accessor first_name = ''
  @observable accessor last_name = ''
  @observable accessor address = ''
  @observable accessor postal_code = ''
  @observable accessor city = ''
  @observable accessor shipping_address = ''
  @observable accessor shipping_postal_code = ''
  @observable accessor shipping_city = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor payment_method = 'cod'
  @observable accessor ship_to_different_address = false

  @observable accessor error_message = '' // TODO: Hook this up to operation
  @observable accessor accept = false

  @action setValue (key, value) {
    this[key] = value
  }
}

export const checkoutState = new CheckoutState()

function WithInputSetting ({ inputKey, children }) {
  let settings_manager = useSettingsManager()
  let inputSettings = settings_manager.getInputSettings(inputKey)

  return children(inputSettings)
}

function useHasMounting () {
  const portmanager = usePortManager()
  return !portmanager.ports.some(p => p.installation !== 'yes')
}

const DisplayTax = observer(() => {
  const price_manager = usePriceManager()
  const port_manager = usePortManager()

  useEffect(() => {
    return reaction(
      () => port_manager.vat,
      vat => {
        price_manager.updatePrice()
      }
    )
  }, [])
  return <div>{port_manager.vat}%</div>
})

const OrderForm = observer(function OrderForm (props) {
  let settings_manager = useSettingsManager()
  const getPersonDescription = function () {
    return settings_manager.getOtherSettings('advisor_description', null)
  }
  const getPersonTitle = function () {
    return settings_manager.getOtherSettings('advisor_name', null)
  }
  const getPersonImage = function () {
    return settings_manager.getOtherSettings('advisor_image', null)
  }
  const leverings_detaljer = settings_manager.getOtherSettings(
    'delivery_details_title',
    'Leverings detaljer'
  )
  const advanced_vat_flow_enabled = settings_manager.getStepSettings('step_5').advanced_vat_flow_enabled
  const paymentGateways = React.useContext(PaymentGateways)

  let inputSettings = settings_manager.getInputSettings('is_company')
  return (
    <div className={'final-step__form'}>
      {checkoutState.error_message === '' ? null : (
        <div
          className={'error'}
          dangerouslySetInnerHTML={{ __html: checkoutState.error_message }}
        />
      )}

      <aside className={'input-wrapper'} data-borderless={false}>
        <FormHeader strings={{ title: leverings_detaljer }} htag={'h2'}/>
        <div className={'input-grid'}>
          {advanced_vat_flow_enabled ? (
            <>
              <aside className={'input-box input-text'} data-size={'full'}>
                <div className={'input-box__title'} data-size={'full'}>
                  {inputSettings.title}
                </div>
                <div className="input-grid">
                  <LocalizedInputRadioBox
                    stateObj={checkoutState}
                    name={'is_company'}
                    size={'half'}
                    {...inputSettings.options['company']}
                    value={true}
                  />
                  <LocalizedInputRadioBox
                    stateObj={checkoutState}
                    name={'is_company'}
                    size={'half'}
                    {...inputSettings.options['private']}
                    value={false}
                  />
                </div>
              </aside>
              {!checkoutState.is_company ? (
                <WithInputSetting inputKey={'is_building_old'}>
                  {inputSettings => (
                    <aside
                      className={'input-box input-text'}
                      data-size={'full'}
                    >
                      <div className={'input-box__title'} data-size={'full'}>
                        {inputSettings.title}
                      </div>
                      <div className="input-grid">
                        <LocalizedInputRadioBox
                          stateObj={checkoutState}
                          name={'is_building_old'}
                          size={'half'}
                          {...inputSettings.options['true']}
                          value={true}
                        />
                        <LocalizedInputRadioBox
                          stateObj={checkoutState}
                          name={'is_building_old'}
                          size={'half'}
                          {...inputSettings.options['false']}
                          value={false}
                        />
                      </div>
                    </aside>
                  )}
                </WithInputSetting>
              ) : null}
              {checkoutState.is_company ? (
                <WithInputSetting inputKey={'vat_number'}>
                  {inputSettings => (
                    <LocalizedInput
                      stateObj={checkoutState}
                      type={'text'}
                      name={'vat_number'}
                      size={'full'}
                      value={inputSettings.title}
                    />
                  )}
                </WithInputSetting>
              ) : null}
              {checkoutState.is_company ? (
                <WithInputSetting inputKey={'should_pay_vat'}>
                  {inputSettings => (
                    <aside
                      className={'input-box input-text'}
                      data-size={'full'}
                    >
                      <div className={'input-box__title'} data-size={'full'}>
                        {inputSettings.title}
                      </div>
                      <div className="input-grid">
                        <LocalizedInputRadioBox
                          stateObj={checkoutState}
                          name={'should_pay_vat'}
                          size={'half'}
                          {...inputSettings.options['true']}
                          value={true}
                        />
                        <LocalizedInputRadioBox
                          stateObj={checkoutState}
                          name={'should_pay_vat'}
                          size={'half'}
                          {...inputSettings.options['false']}
                          value={false}
                        />
                      </div>
                    </aside>
                  )}
                </WithInputSetting>
              ) : null}
              <DisplayTax/>
            </>
          ) : null}
          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'company'}
            size={'full'}
          />
          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'first_name'}
            size={'half'}
            value={'Fornavn'}
          />
          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'last_name'}
            size={'half'}
            value={'Efternavn'}
          />

          <InputDivider/>

          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'address'}
            size={'full'}
            value={'Adresse'}
          />
          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'postal_code'}
            size={'half'}
            value={'Postnummer'}
          />
          <LocalizedInput
            stateObj={checkoutState}
            type={'text'}
            name={'city'}
            size={'half'}
            value={'By'}
          />
          <div className="input-box" data-size={'full'}>
            <button
              className={'link-button link-button--icon'}
              onClick={() =>
                (checkoutState.setValue('ship_to_different_address', !checkoutState.ship_to_different_address))
              }
            >
              <SVGIcon
                type={'add-alt'}
                fill={'#000'}
                className={
                  'link-button__icon' +
                  (checkoutState.ship_to_different_address ? ' hidden' : '')
                }
              />
              <SVGIcon
                type={'remove-alt'}
                fill={'#000'}
                className={
                  'link-button__icon' +
                  (checkoutState.ship_to_different_address ? '' : ' hidden')
                }
              />
              {settings_manager.getOtherSettings(
                'ship_to_different_address',
                'Tilføj anden leveringsadresse'
              )}
            </button>
          </div>
          {checkoutState.ship_to_different_address ? (
            <React.Fragment>
              <InputDivider/>

              <LocalizedInput
                stateObj={checkoutState}
                type={'text'}
                name={'shipping_address'}
                size={'full'}
                value={'Leverings Adresse'}
              />
              <LocalizedInput
                stateObj={checkoutState}
                type={'text'}
                name={'shipping_postal_code'}
                size={'half'}
                value={'Leverings Postnummer'}
              />
              <LocalizedInput
                stateObj={checkoutState}
                type={'text'}
                name={'shipping_city'}
                size={'half'}
                value={'Leverings By'}
              />
            </React.Fragment>
          ) : (
            []
          )}
          <InputDivider/>

          <LocalizedInput
            stateObj={checkoutState}
            type={'email'}
            name={'email'}
            size={'half'}
            value={'Email'}
          />
          <LocalizedInput
            stateObj={checkoutState}
            type={'tel'}
            name={'phone'}
            size={'half'}
            value={'Telefon'}
          />

          <aside className={'input-box input-text'} data-size={'full'}>
            <div className={'input-box__title'}>
              {settings_manager.getOtherSettings(
                'payment_method_label',
                'Betalings metode'
              )}
            </div>
            <div className="input-grid">
              {Object.entries(paymentGateways).map(([key, val]) => {
                return (
                  <aside
                    className="input-radio-box input-box input-box--center"
                    data-size="half"
                    key={key}
                  >
                    <input
                      id={key}
                      type="radio"
                      name="payment_method"
                      value={key}
                      onChange={() => (checkoutState.setValue('payment_method', key))}
                      checked={checkoutState.payment_method === key}
                    />

                    <label htmlFor={key}>
                      <div
                        className="input-box__title"
                        style={{ marginBottom: 0 }}
                      >
                        {val.title}
                      </div>
                      <span
                        dangerouslySetInnerHTML={{ __html: val.description }}
                      />
                    </label>
                  </aside>
                );
              })}
            </div>
          </aside>
        </div>
        <AcceptTerms/>
      </aside>
      <PersonInfo
        image={getPersonImage()}
        title={getPersonTitle()}
        description={getPersonDescription()}
      />
    </div>
  );
});

export default OrderForm
